import { useEffect, useRef, useState } from 'react'

interface UnsavedChangesGuard {
  hasUnsavedChanges: boolean
  setSectionDirty: (section: string | number, isDirty: boolean) => void
  clear: () => void
  /** Runs `action` now when nothing is unsaved, otherwise holds it until the prompt is confirmed. */
  guard: (action: () => void) => void
  pendingAction: (() => void) | null
  confirmPending: () => void
  dismissPending: () => void
}

/**
 * The app mounts a `BrowserRouter`, so React Router's `useBlocker` is unavailable (it needs a data
 * router). This guards the page's own navigation triggers plus a tab close; a navigation started
 * anywhere else — a nav-bar link, the browser Back button — is not intercepted.
 */
export const useUnsavedChanges = (): UnsavedChangesGuard => {
  const [dirtySections, setDirtySections] = useState<Array<string | number>>([])
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null)
  const hasUnsavedChanges = dirtySections.length > 0
  const hasUnsavedChangesRef = useRef(hasUnsavedChanges)
  hasUnsavedChangesRef.current = hasUnsavedChanges

  useEffect(() => {
    const warnBeforeUnload = (event: BeforeUnloadEvent): void => {
      if (!hasUnsavedChangesRef.current) return

      event.preventDefault()
      event.returnValue = ''
    }

    window.addEventListener('beforeunload', warnBeforeUnload)
    return () => { window.removeEventListener('beforeunload', warnBeforeUnload) }
  }, [])

  const setSectionDirty = (section: string | number, isDirty: boolean): void => {
    setDirtySections((sections) => {
      if (isDirty === sections.includes(section)) return sections

      return isDirty ? [...sections, section] : sections.filter((s) => s !== section)
    })
  }

  const guard = (action: () => void): void => {
    if (!hasUnsavedChanges) {
      action()
      return
    }

    setPendingAction(() => action)
  }

  const confirmPending = (): void => {
    const action = pendingAction
    setPendingAction(null)
    setDirtySections([])
    action?.()
  }

  return {
    hasUnsavedChanges,
    setSectionDirty,
    clear: () => { setDirtySections([]) },
    guard,
    pendingAction,
    confirmPending,
    dismissPending: () => { setPendingAction(null) }
  }
}
